'use client'

import React, { ReactElement } from 'react'
import { BasicHotelInfo } from '@/lib/backend-api'
import { Rating } from '@/components/ui/search/SearchResults'
import { Domain } from '../../../domain-config/domain-config'
import Button from '@/components/common/button/Button'
import classNames from 'classnames'
import { formatHotelUrl } from '@/lib/utils/utils'
import { placeWithinSearchRadius } from '@/lib/mapUtils'
import { ButtonIcon } from '@/components/common/button/constants'
import { Picture } from '@/components/common/picture/Picture'
import { Sizes } from '@/components/common/picture/Sizes'
import { HotelName } from '@/components/ui/search/HotelName'
import { HotelNameSize } from '@/components/ui/search/HotelNameSize'
import { Destinations } from '@/components/ui/search/Destinations'

const SIZES: Record<Sizes, [number, number, number]> = {
  [Sizes.xs]: [140, 140, 140],
  [Sizes.sm]: [140, 140, 140],
  [Sizes.md]: [140, 140, 140],
  [Sizes.lg]: [140, 140, 140],
  [Sizes.xl]: [140, 140, 140],
}

interface NotFoundHotelPopupProps {
  hotel: BasicHotelInfo
  domain: Domain
  withinModal?: boolean
  t: (key: string) => string
}

export function NotFoundHotelPopup(props: NotFoundHotelPopupProps): ReactElement {
  function hotelWithinSearchRadius(): boolean {
    return placeWithinSearchRadius(props.hotel.Longitude, props.hotel.Latitude)
  }

  function openHotelPage(e: React.MouseEvent): false {
    window.open(formatHotelUrl(props.hotel.Url), '_blank')
    e.preventDefault()
    e.stopPropagation()

    return false
  }

  return (
    <div
      id="not-found-hotels-modal"
      className={classNames(
        'h:[171px] relative grid max-w-[calc(100vw-10px)] grid-cols-[auto] overflow-hidden font-sans',
        {
          'grid-rows-[30px,120px,auto] xxl:grid-cols-[160px_auto]':
            hotelWithinSearchRadius() && props.hotel.RatingValue,
          'grid-rows-[auto,auto] xxl:grid-cols-[120px_auto]':
            !hotelWithinSearchRadius() && props.hotel.RatingValue,
          'grid-rows-[30px,130px,auto] md:grid-rows-[30px,110px,auto] xxl:grid-cols-[160px_auto]':
            hotelWithinSearchRadius() && !props.hotel.RatingValue,
          'grid-rows-[auto,auto] xxl:grid-cols-[130px_auto]':
            !hotelWithinSearchRadius() && !props.hotel.RatingValue,
        },
      )}
    >
      {hotelWithinSearchRadius() && (
        <div className="col-start-1 row-start-1 flex min-w-full items-center rounded-tr bg-white px-2 pt-2 text-xs text-stone-600 xxl:col-start-2">
          <svg
            viewBox="0 0 17 15"
            className="mr-2 h-[13px]"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.3457 8.71484V13.8711H7.0957V10.1211C7.0957 9.87245 7.19448 9.634 7.37029 9.45818C7.54611 9.28237 7.78456 9.18359 8.0332 9.18359H8.9707C9.21934 9.18359 9.4578 9.28237 9.63362 9.45818C9.80943 9.634 9.9082 9.87245 9.9082 10.1211V13.8711H13.6582V8.71484"
              stroke="#5C5C5C"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M1.4707 7.77745L7.83883 1.40932C7.92589 1.3222 8.02927 1.25309 8.14305 1.20594C8.25683 1.15879 8.37879 1.13452 8.50195 1.13452C8.62512 1.13452 8.74708 1.15879 8.86086 1.20594C8.97464 1.25309 9.07802 1.3222 9.16508 1.40932L15.5332 7.77745"
              stroke="#5C5C5C"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {props.t('mapPopupInSearchRadius')}
        </div>
      )}
      <div
        className="relative col-start-1 row-span-2 row-start-1 hidden min-w-full cursor-pointer rounded-tl bg-white xxl:block"
        onClick={openHotelPage}
      >
        <Picture
          src={props.hotel.Images[0]}
          alt={props.hotel.HotelName}
          size={SIZES}
          cornerRounding="rounded-lt-md"
        />
      </div>
      <div
        className={classNames(
          'flex min-w-full cursor-pointer flex-col whitespace-nowrap rounded-tl rounded-tr border-b border-[#DDDDDD] border-b-light-grey bg-white px-2 xxl:rounded-tl-none',
          {
            'row-start-2 pt-1': hotelWithinSearchRadius(),
            'row-start-1 pt-2': !hotelWithinSearchRadius(),
          },
        )}
        onClick={openHotelPage}
      >
        <div className="overflow-hidden">
          <HotelName
            tag="span"
            domain={props.domain}
            stars={4}
            categoryText={'[first class]'}
            skeleton={false}
            hotelName={props.hotel.HotelName}
            size={HotelNameSize.SMALL}
            additionalClassName="col-span-2"
            allowWrap={props.withinModal}
          />
        </div>

        <Destinations destinations={props.hotel.Destinations} classNamesItems="whitespace-nowrap" />
        <div
          className={classNames('flex h-[52px] flex-row items-center', {
            'justify-between': props.hotel.RatingValue,
            'justify-center': !props.hotel.RatingValue,
          })}
        >
          <Rating
            skeleton={false}
            ratingText={props.hotel.RatingText}
            ratingValue={props.hotel.RatingValue}
            additionalClassNames="mb-1.5"
          />
          <Button icon={ButtonIcon.ARROW_RIGHT_SLIM} className="h-[36px] text-white">
            {props.t('mapPopupOpenHotel')}
          </Button>
        </div>
      </div>
      <div className="col-span-1 row-start-3 rounded-b bg-white p-1 text-sm text-color-highlight-red-lightened xxl:col-span-2 xxl:col-start-1 ">
        <svg
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="ml-1 mr-2 inline-block h-3.5 w-3.5 -translate-y-[1px]"
        >
          <path
            d="M6.75 12.5C9.92564 12.5 12.5 9.92564 12.5 6.75C12.5 3.57436 9.92564 1 6.75 1C3.57436 1 1 3.57436 1 6.75C1 9.92564 3.57436 12.5 6.75 12.5Z"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="stroke-color-highlight-red-lightened"
          />
          <path
            d="M2.68506 10.8151L10.8148 2.68384"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="stroke-color-highlight-red-lightened"
          />
        </svg>
        {props.t('mapPopupNotMatchingCriteria')}
      </div>
    </div>
  )
}
